/* eslint-disable sort-keys */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
} from '@material-ui/core';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import api, { post, put } from '@lib/ApiClient';
import store from '@lib/store';
import { Box, Loading } from '@atoms';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  admin: boolean;
  disabled: boolean;
  disabled_at: string | null;
  organizations: { id: string; name: string }[];
  created_at: string;
}

interface Organization {
  id: string;
  name: string;
}

export const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [admin, setAdmin] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Authorization is the API's job now. It used to be a shared password typed
  // into this browser and sent as a request parameter — anyone who learned the
  // string held full access to every user in every organization, and it was
  // kept in localStorage in the clear. The server no longer reads those
  // parameters at all; it checks the signed-in user's own admin flag.
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [usersData, orgsData] = await Promise.all([
        api<AdminUser[]>('admin/users'),
        api<Organization[]>('admin/organizations'),
      ]);
      setUsers(usersData || []);
      setOrganizations(orgsData || []);
    } catch (e: any) {
      // 403 means this account is not a platform admin. Anything else is a real
      // fault and should say so rather than bouncing the user out with no
      // explanation, which is what made the old failure mode so confusing.
      if (e?.response?.status === 403) {
        navigate('/');
        return;
      }
      setError('Could not load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!store.user?.admin) {
      navigate('/');
      return;
    }
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = () => {
    setEditingUser(null);
    setName('');
    setEmail('');
    setPhone('');
    setAdmin(false);
    setDisabled(false);
    setOrganizationId('');
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setEditingUser(user);
    setName(user.name || '');
    setEmail(user.email || '');
    setPhone(user.phone || '');
    setAdmin(user.admin);
    setDisabled(user.disabled);
    setOrganizationId('');
    setError('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      if (editingUser) {
        await put(`admin/users/${editingUser.id}`, {
          admin,
          disabled,
          email,
          name,
          phone,
        });
      } else {
        await post('admin/users', {
          admin,
          email,
          name,
          organization_id: organizationId,
          phone,
        });
      }
      handleClose();
      fetchUsers();
    } catch (e: any) {
      const errorData = await e.response?.json?.();
      setError(errorData?.error || errorData?.errors?.join(', ') || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 180 },
    { field: 'email', headerName: 'Email', width: 220 },
    { field: 'phone', headerName: 'Phone', width: 140 },
    {
      field: 'admin',
      headerName: 'Admin',
      renderCell: params => (params.value ? <Chip size="small" label="Admin" color="primary" /> : null),
      width: 100,
    },
    {
      field: 'disabled',
      headerName: 'Status',
      renderCell: params =>
        params.value ? (
          <Chip size="small" label="Disabled" color="secondary" />
        ) : (
          <Chip size="small" label="Active" style={{ backgroundColor: '#4caf50', color: 'white' }} />
        ),
      width: 120,
    },
    {
      field: 'organizations',
      headerName: 'Organizations',
      valueGetter: params => {
        const orgs = params.value as { id: string; name: string }[] | undefined;
        return orgs?.map(o => o.name).join(', ') || '';
      },
      width: 200,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: params => (
        <Button
          size="small"
          variant="outlined"
          onClick={e => {
            e.stopPropagation();
            openEditDialog(params.row as AdminUser);
          }}
        >
          Edit
        </Button>
      ),
      width: 100,
    },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <Box fullWidth fill column px={2} flexGrow={1} bgcolor="background.dark" color="text.primary">
      <Box py={2} style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
        <Button color="primary" onClick={() => navigate('/')} variant="outlined">
          Return to Dashboard
        </Button>
        <h2 style={{ margin: 0 }}>User Management</h2>
        <Button color="primary" onClick={openCreateDialog} variant="contained">
          + New User
        </Button>
      </Box>

      <DataGrid
        rows={users}
        columns={columns}
        pageSize={25}
        rowsPerPageOptions={[25]}
        style={{ backgroundColor: 'white', flexGrow: 1 }}
        onRowClick={params => openEditDialog(params.row as AdminUser)}
      />

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          {error && (
            <Box py={1}>
              <div style={{ backgroundColor: '#ffebee', borderRadius: '4px', color: '#f44336', padding: '8px' }}>
                {error}
              </div>
            </Box>
          )}
          <Box py={1}>
            <TextField fullWidth label="Name" value={name} onChange={e => setName(e.target.value)} variant="outlined" />
          </Box>
          <Box py={1}>
            <TextField fullWidth label="Email" value={email} onChange={e => setEmail(e.target.value)} variant="outlined" />
          </Box>
          <Box py={1}>
            <TextField fullWidth label="Phone" value={phone} onChange={e => setPhone(e.target.value)} variant="outlined" placeholder="+1234567890" />
          </Box>
          <Box py={1}>
            <FormControlLabel
              control={<Checkbox checked={admin} onChange={e => setAdmin(e.target.checked)} />}
              label="System Admin"
            />
          </Box>
          {editingUser && (
            <Box py={1}>
              <FormControlLabel
                control={<Checkbox checked={disabled} onChange={e => setDisabled(e.target.checked)} />}
                label="Disabled"
              />
            </Box>
          )}
          {!editingUser && organizations.length > 1 && (
            <Box py={1}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Organization</InputLabel>
                <Select value={organizationId} onChange={e => setOrganizationId(e.target.value as string)} label="Organization">
                  <MenuItem value="">
                    <em>Current Organization</em>
                  </MenuItem>
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
